import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NzContentComponent, NzLayoutComponent } from 'ng-zorro-antd/layout';
import { AnalyticsService } from './service/analytics.service';
import { MenuComponent } from './component/menu/menu.component';
import { Subscription } from 'rxjs';
import { FooterComponent } from './component/footer/footer.component';
import { IconModule } from './icon-module';
import loadjs from 'loadjs';
import { unsubscribe } from './util/rx.util';
import { SentryService } from './service/sentry.service';
import { ConfigService } from './service/config.service';
import { Env } from './dto/enum/env.enum';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-root',
  imports: [
    IconModule,
    RouterOutlet,
    NzLayoutComponent,
    NzContentComponent,
    MenuComponent,
    FooterComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.less',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly router: Router = inject(Router);
  private readonly analyticsService: AnalyticsService =
    inject(AnalyticsService);
  private readonly document: Document = inject<Document>(DOCUMENT);
  private readonly platformId: object = inject(PLATFORM_ID);
  private readonly sentryService: SentryService = inject(SentryService);
  private readonly configService: ConfigService = inject(ConfigService);
  private routerSub$?: Subscription = undefined;

  public ngOnDestroy(): void {
    unsubscribe(this.routerSub$);
  }

  public ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.analyticsService.event('entrypoint', {
      path: this.router.url,
    });
    this.routerSub$ = this.router.events.subscribe({
      next: (event: Event): void => {
        if (event instanceof NavigationEnd) {
          this.analyticsService.event('navigation_end', {
            path: event.urlAfterRedirects,
          });
        }
      },
    });

    this.loadAdUtilScript();

    if (Env.PROD === this.configService.env) {
      this.loadMoneyTagBannerScript();
    }
  }

  private loadAdUtilScript(): void {
    if (this.document.getElementById('adutil')) {
      return;
    }

    loadjs('adutil.js', {
      async: true,
      numRetries: 1,
      success: (): void => {
        this.analyticsService.event('adblock_exec', {
          enabled: false,
        });
      },
      error: (): void => {
        this.analyticsService.event('adblock_exec', {
          enabled: true,
        });
      },
      before: (path: string, scriptEl: HTMLElement): boolean => {
        const htmlElement: HTMLElement | null =
          this.document.getElementById('adutil');
        if (htmlElement) {
          htmlElement.parentElement?.removeChild(htmlElement);
        }
        scriptEl.id = 'adutil';
        this.document.body.appendChild(scriptEl);
        return false;
      },
    });
  }

  private loadMoneyTagBannerScript(): void {
    if (this.document.getElementById('moneytag')) {
      return;
    }

    loadjs('https://gizokraijaw.net/401/9441167', {
      async: true,
      numRetries: 1,
      error: (): void => {
        console.error('An error occurred during loading MoneyTag script');
        this.sentryService.captureException({
          error: 'An error occurred during loading MoneyTag script',
          severityLevel: 'warning',
          transactionName: 'LOAD_MONEYTAG_SCRIPT_ERROR',
        });
      },
      before: (path: string, scriptEl: HTMLElement): boolean => {
        const htmlElement: HTMLElement | null =
          this.document.getElementById('moneytag');
        if (htmlElement) {
          htmlElement.parentElement?.removeChild(htmlElement);
        }
        scriptEl.id = 'moneytag';
        this.document.body.appendChild(scriptEl);
        return false;
      },
    });
  }
}
